import { useState } from 'react';
import { ShoppingBag, RefreshCw, Download, Package } from 'lucide-react';
import PageHeader from '@/components/ui/PageHeader';
import PageShell from '@/components/ui/PageShell';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import {
  formatFulfillmentStatus,
  formatFinancialStatus,
  getStatusColor,
} from '@/lib/shopifyClient';
import { useShopifySync } from '@/lib/useShopifySync';

const STATUS_FILTERS = [
  { value: 'any', label: 'Alle' },
  { value: 'open', label: 'Offen' },
  { value: 'closed', label: 'Abgeschlossen' },
  { value: 'cancelled', label: 'Storniert' },
];

const COLOR_MAP = {
  green: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  yellow: 'bg-amber-50 text-amber-700 border border-amber-200',
  orange: 'bg-orange-50 text-orange-700 border border-orange-200',
  red: 'bg-red-50 text-red-700 border border-red-200',
  gray: 'bg-slate-100 text-slate-600 border border-slate-200',
};

const LIFECYCLE_BADGE = {
  Neu: 'bg-blue-50 text-blue-700 border border-blue-200',
  Kommissioniert: 'bg-indigo-50 text-indigo-700 border border-indigo-200',
  Versandbereit: 'bg-cyan-50 text-cyan-700 border border-cyan-200',
  Unterwegs: 'bg-amber-50 text-amber-700 border border-amber-200',
  Geliefert: 'bg-teal-50 text-teal-700 border border-teal-200',
  Berechnet: 'bg-purple-50 text-purple-700 border border-purple-200',
  Abgeschlossen: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  Storniert: 'bg-red-50 text-red-700 border border-red-200',
};

/** GraphQL enum (PAID, UNFULFILLED, …) → REST tarzı lowercase değer */
function toRestStatus(value, kind) {
  if (!value) return kind === 'fulfillment' ? 'unfulfilled' : null;
  const v = String(value).toLowerCase();
  if (v === 'partially_fulfilled') return 'partial';
  return v;
}

/** Supabase kaydını görünüm modeline çevirir */
function toView(o) {
  let items = o.positionen ?? o.items ?? [];
  if (typeof items === 'string') {
    try { items = JSON.parse(items); } catch { items = []; }
  }
  return {
    dbId: o.id,
    source: o.source,
    platformId: o.platform_order_id,
    orderNumber: (o.order_number || '').replace(/^SH-/, ''),
    customerName: o.lead_name || 'Unbekannt',
    email: o.lead_email || '',
    address: o.lieferadresse || o.lead_address || '',
    createdAt: o.created_date,
    lifecycleStatus: o.status,
    financialStatus: toRestStatus(o.shopify_financial_status, 'financial'),
    fulfillmentStatus: toRestStatus(o.shopify_fulfillment_status, 'fulfillment'),
    totalPrice: parseFloat(o.total ?? o.gesamtsumme ?? 0),
    subtotal: parseFloat(o.subtotal ?? 0),
    currency: 'EUR',
    zahlungsart: o.zahlungsart,
    notizen: o.notizen,
    items: Array.isArray(items) ? items : [],
  };
}

export default function AdminShopifyOrders() {
  const [statusFilter, setStatusFilter] = useState('any');
  const [search, setSearch] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Senkron kontrolü — periyodik interval Layout'taki instance'da (enabled: false)
  const { syncing, lastResult, progressMsg, runSync, lastSyncAt } = useShopifySync({
    enabled: false,
  });

  // Kaynak: Supabase (senkronlanmış TAM liste — canlı API'nin 100 kayıt limiti yok)
  const { data: rawOrders = [], isLoading: loading, error, refetch } = useQuery({
    queryKey: ['orders', 'shopify-synced'],
    queryFn: () => base44.entities.Order.filter({ source: ['shopify', 'ebay'] }, '-created_date'),
  });

  const orders = rawOrders.map(toView);

  const filtered = orders.filter((o) => {
    if (statusFilter === 'open'
        && ['Abgeschlossen', 'Storniert'].includes(o.lifecycleStatus)) return false;
    if (statusFilter === 'closed' && o.lifecycleStatus !== 'Abgeschlossen') return false;
    if (statusFilter === 'cancelled' && o.lifecycleStatus !== 'Storniert') return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      o.orderNumber.toLowerCase().includes(q) ||
      o.email.toLowerCase().includes(q) ||
      o.customerName.toLowerCase().includes(q) ||
      (o.platformId || '').includes(q)
    );
  });

  const totalRevenue = filtered.reduce((sum, o) => sum + (o.totalPrice || 0), 0);

  return (
    <PageShell
      head={(
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <PageHeader
              icon={<ShoppingBag className="w-5 h-5" />}
              title="Shopify Bestellungen"
              subtitle="inmotion-handel.myshopify.com · synchronisierte Datenbank"
            />
    
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => refetch()}
                disabled={loading}
                className="flex items-center gap-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 px-4 py-2.5 min-h-[44px] rounded-lg text-sm font-medium transition-colors"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Lade...' : 'Aktualisieren'}
              </button>
              <button
                onClick={() => runSync({ silent: false })}
                disabled={syncing}
                className="flex items-center gap-2 bg-brand-950 hover:bg-brand-900 text-white px-4 py-2.5 min-h-[44px] rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
                title="Neue/geänderte Bestellungen von Shopify übernehmen"
              >
                <Download className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? progressMsg || 'Synchronisiere…' : 'Sync → Lager'}
              </button>
              <button
                onClick={() => runSync({ full: true, silent: false })}
                disabled={syncing}
                className="flex items-center gap-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 px-4 py-2.5 min-h-[44px] rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                title="ALLE Shopify-Bestellungen importieren (Erstimport / Reparatur)"
              >
                <Package className="w-4 h-4" /> Vollimport
              </button>
            </div>
          </div>
      )}
    >

      {/* Sync status bar */}
      <div className="flex flex-wrap items-center gap-3 mb-6 text-xs text-slate-500 bg-white border border-slate-200 rounded-lg px-4 py-2">
        <span>
          Letzter Sync:{' '}
          {lastSyncAt ? new Date(lastSyncAt).toLocaleString('de-DE') : 'noch nie'}
        </span>
        {lastResult && (
          <span className="text-emerald-600">
            +{lastResult.imported} neu · {lastResult.updated} aktualisiert
            {lastResult.cancelled > 0 && ` · ${lastResult.cancelled} storniert`}
            {lastResult.relinked > 0 && ` · ${lastResult.relinked} Position(en) nachträglich mit Produkt verknüpft`}
            {lastResult.skipped > 0 && ` · ${lastResult.skipped} übersprungen`}
          </span>
        )}
        <span className="ml-auto text-slate-500">
          Auto-Sync alle 3 Min. aktiv — neue Bestellungen erzeugen automatisch einen Lagerauftrag (Status „Neu“)
        </span>
      </div>

      {/* Stats */}
      {!loading && !error && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Gesamt', value: filtered.length, icon: '📦' },
            {
              label: 'Umsatz',
              value: `€ ${totalRevenue.toLocaleString('de-DE', { minimumFractionDigits: 2 })}`,
              icon: '💶',
            },
            {
              label: 'Offen (Lager)',
              value: filtered.filter((o) => o.lifecycleStatus === 'Neu').length,
              icon: '⏳',
            },
            {
              label: 'Abgeschlossen',
              value: filtered.filter((o) => o.lifecycleStatus === 'Abgeschlossen').length,
              icon: '✅',
            },
          ].map((stat) => (
            <div
              key={stat.label}
              className="bg-white border border-slate-200 rounded-2xl p-4"
            >
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{stat.label}</p>
              <p className="mt-1 text-3xl font-display font-extrabold tracking-tight tabular-nums text-slate-900">{stat.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 mb-5">
        <input
          type="text"
          placeholder="Suche (Nummer, E-Mail, Name, Platform-ID...)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500"
        />
        <div className="flex gap-2">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setStatusFilter(f.value)}
              className={`px-3 py-2 min-h-[44px] rounded-lg text-xs font-medium transition-all ${
                statusFilter === f.value
                  ? 'bg-slate-800 text-white border border-slate-800'
                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-5 text-red-700 text-sm">
          <strong>⚠️ Fehler:</strong> {error.message}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16 text-slate-500">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 mx-auto mb-3 animate-spin text-slate-400" />
            <div className="text-sm">Bestellungen werden geladen...</div>
          </div>
        </div>
      )}

      {/* Orders Table */}
      {!loading && !error && (
        <>
          {filtered.length === 0 ? (
            <div className="text-center py-16 text-slate-500">
              <Package className="w-8 h-8 mx-auto mb-3 text-slate-300" />
              <div>Keine Bestellungen gefunden</div>
              <div className="text-xs mt-2">
                Tipp: „Vollimport“ lädt alle Bestellungen aus Shopify.
              </div>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              {/* Table Header */}
              <div className="hidden md:grid grid-cols-8 gap-4 px-5 py-3 border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                <div>Bestellung</div>
                <div className="col-span-2">Kunde</div>
                <div>Datum</div>
                <div>Zahlung</div>
                <div>Versand</div>
                <div>Auftrag</div>
                <div className="text-right">Betrag</div>
              </div>

              {/* Rows */}
              <div className="divide-y divide-slate-200">
                {filtered.map((order) => {
                  const color = getStatusColor(order.financialStatus, order.fulfillmentStatus);
                  return (
                    <div
                      key={order.dbId}
                      onClick={() => setSelectedOrder(order)}
                      className="grid grid-cols-2 md:grid-cols-8 gap-2 md:gap-4 px-4 md:px-5 py-4 cursor-pointer hover:bg-slate-50/50 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-slate-900">#{order.orderNumber}</span>
                        <span
                          className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${
                            order.source === 'ebay'
                              ? 'bg-amber-50 text-amber-700 border border-amber-200'
                              : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          }`}
                        >
                          {order.source === 'ebay' ? 'eBay' : 'Shopify'}
                        </span>
                      </div>

                      <div className="md:col-span-2">
                        <div className="text-sm font-medium text-slate-900 truncate">
                          {order.customerName}
                        </div>
                        {order.email && (
                          <div className="text-xs text-slate-500 truncate">{order.email}</div>
                        )}
                      </div>

                      <div className="text-xs text-slate-500">
                        {order.createdAt &&
                          new Date(order.createdAt).toLocaleDateString('de-DE', {
                            day: '2-digit',
                            month: '2-digit',
                            year: '2-digit',
                          })}
                      </div>

                      <div>
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${COLOR_MAP[color]}`}>
                          {formatFinancialStatus(order.financialStatus)}
                        </span>
                      </div>

                      <div>
                        <span
                          className={`text-xs px-2 py-1 rounded-full font-medium ${
                            order.fulfillmentStatus === 'fulfilled'
                              ? COLOR_MAP.green
                              : COLOR_MAP.yellow
                          }`}
                        >
                          {formatFulfillmentStatus(order.fulfillmentStatus)}
                        </span>
                      </div>

                      <div>
                        <span
                          className={`text-xs px-2 py-1 rounded-full font-medium ${
                            LIFECYCLE_BADGE[order.lifecycleStatus] || COLOR_MAP.gray
                          }`}
                        >
                          {order.lifecycleStatus}
                        </span>
                      </div>

                      <div className="text-right font-bold text-slate-900">
                        {order.totalPrice.toLocaleString('de-DE', { minimumFractionDigits: 2 })}{' '}
                        {order.currency}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <div>
                <h2 className="text-xl font-bold text-slate-900">
                  Bestellung #{selectedOrder.orderNumber}
                </h2>
                <p className="text-sm text-slate-500">
                  {selectedOrder.createdAt &&
                    new Date(selectedOrder.createdAt).toLocaleString('de-DE')}
                </p>
              </div>
              <button
                onClick={() => setSelectedOrder(null)}
                className="text-slate-500 hover:text-slate-900 text-2xl leading-none"
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 space-y-5">
              {/* Status */}
              <div className="flex flex-wrap gap-3">
                <span
                  className={`text-sm px-3 py-1 rounded-full font-medium ${
                    COLOR_MAP[
                      getStatusColor(selectedOrder.financialStatus, selectedOrder.fulfillmentStatus)
                    ]
                  }`}
                >
                  {formatFinancialStatus(selectedOrder.financialStatus)}
                </span>
                <span
                  className={`text-sm px-3 py-1 rounded-full font-medium ${
                    selectedOrder.fulfillmentStatus === 'fulfilled'
                      ? COLOR_MAP.green
                      : COLOR_MAP.yellow
                  }`}
                >
                  {formatFulfillmentStatus(selectedOrder.fulfillmentStatus)}
                </span>
                <span
                  className={`text-sm px-3 py-1 rounded-full font-medium ${
                    LIFECYCLE_BADGE[selectedOrder.lifecycleStatus] || COLOR_MAP.gray
                  }`}
                >
                  Lager: {selectedOrder.lifecycleStatus}
                </span>
              </div>

              {/* Customer */}
              <div className="bg-slate-50 rounded-xl p-4">
                <h3 className="text-xs font-semibold text-slate-500 uppercase mb-2">Kunde</h3>
                <div className="text-sm text-slate-900">{selectedOrder.customerName}</div>
                {selectedOrder.email && (
                  <div className="text-sm text-slate-500 mt-1">{selectedOrder.email}</div>
                )}
                {selectedOrder.address && (
                  <div className="text-sm text-slate-500 mt-1">{selectedOrder.address}</div>
                )}
                {selectedOrder.zahlungsart && (
                  <div className="text-sm text-slate-500 mt-1">
                    Zahlung: {selectedOrder.zahlungsart}
                  </div>
                )}
              </div>

              {/* Line Items */}
              <div>
                <h3 className="text-xs font-semibold text-slate-500 uppercase mb-3">
                  Artikel ({selectedOrder.items.length})
                </h3>
                <div className="space-y-2">
                  {selectedOrder.items.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex justify-between items-start bg-slate-50 rounded-lg p-3"
                    >
                      <div className="flex-1">
                        <div className="text-sm font-medium text-slate-900">{item.product_name}</div>
                        {(item.variant_label || item.variant_value) && (
                          <div className="text-xs text-slate-500">{item.variant_label || item.variant_value}</div>
                        )}
                        <div className="text-xs text-slate-500">
                          SKU: {item.product_sku || '—'}
                          {item.product_id ? ' · ✅ Produkt verknüpft' : ' · ⚠️ kein Produkt-Match'}
                        </div>
                      </div>
                      <div className="text-right ml-4">
                        <div className="text-sm font-bold text-slate-900">
                          {parseFloat(item.unit_price || 0).toLocaleString('de-DE', {
                            minimumFractionDigits: 2,
                          })}{' '}
                          {selectedOrder.currency}
                        </div>
                        <div className="text-xs text-slate-500">× {item.quantity}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Totals */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2">
                <div className="flex justify-between text-sm text-slate-500">
                  <span>Zwischensumme</span>
                  <span>
                    {selectedOrder.subtotal.toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                    })}{' '}
                    {selectedOrder.currency}
                  </span>
                </div>
                <div className="flex justify-between text-base font-bold text-slate-900 border-t border-slate-200 pt-2">
                  <span>Gesamt</span>
                  <span>
                    {selectedOrder.totalPrice.toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                    })}{' '}
                    {selectedOrder.currency}
                  </span>
                </div>
              </div>

              {/* Notes */}
              {selectedOrder.notizen && (
                <div className="bg-slate-50 rounded-xl p-4">
                  <h3 className="text-xs font-semibold text-slate-500 uppercase mb-2">Notizen</h3>
                  <div className="text-sm text-slate-600 whitespace-pre-wrap">
                    {selectedOrder.notizen}
                  </div>
                </div>
              )}

              {/* Shopify Link */}
              <a
                href={`https://admin.shopify.com/store/inmotion-handel/orders/${selectedOrder.platformId}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 justify-center w-full py-3 min-h-[44px] bg-white border border-slate-200 text-slate-700 rounded-xl text-sm font-semibold hover:bg-slate-50 transition-colors"
              >
                <ShoppingBag className="w-4 h-4 text-emerald-600" /> In Shopify öffnen ↗
              </a>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}
